import { type FC, useState } from 'react';
import { useIntl, FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { Link } from 'dibs-elements/exports/Link';
import { trackEvent } from 'dibs-tracking';
import MagnifyingGlass from 'dibs-icons/exports/legacy/MagnifyingGlass';
import NftLogo from 'dibs-icons/exports/legacy/NftLogo';
import Logo2020 from 'dibs-icons/exports/legacy/Logo2020';
import LogoMonogram from 'dibs-icons/exports/legacy/LogoMonogram';
import { EthereumWalletStatusLazy } from 'dibs-crypto-utils/exports/EthereumWalletStatusLazy';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import { pageTypeMessages, type PageType, SINGLE_EXHIBITION, FEATURED, SEARCH } from './pageType';
import { NftNavBar } from './NftNavBar/NftNavBar';
import { NftSearchBar } from './NftSearchBar/NftSearchBar';
import { NftSearchBarMobile } from './NftSearchBar/NftSearchBarMobile';
import { usePageType } from './hooks/usePageType';
import { useAvailablePageTypes } from './hooks/useAvailablePageTypes';
import { useDeviceType } from './hooks/useDeviceType';
import { type NftNav_viewer$key } from './__generated__/NftNav_viewer.graphql';
import styles from './NftNav.scss';

function trackNavImpression(pageType: string): void {
    trackEvent({
        category: 'promo interaction',
        action: 'NFT marketplace click',
        label: `${pageType} module`,
    });
}
function makeHref(tabName?: string, previewId?: string): string {
    const tabPath = tabName ? `${tabName}` : '';
    const previewIdParam = previewId ? `?preview_id=${previewId}` : '';
    return `/nft-collections/${tabPath}/${previewIdParam}`;
}

export const NftNav: FC<{ isNftLandingPage: boolean; viewer: NftNav_viewer$key }> = ({
    isNftLandingPage,
    viewer: viewerRef,
}) => {
    const viewer = useFragment(
        graphql`
            fragment NftNav_viewer on Viewer {
                nftMintBlogEnabled: featureFlag(feature: "nftMintBlog")
                nftSearchEnabled: featureFlag(feature: "nftSearch")
                ...NftSearchBar_viewer
                ...NftSearchBarMobile_viewer
            }
        `,
        viewerRef
    );
    const { isMobile, deviceType } = useDeviceType();
    const isTablet = deviceType === 'tablet';
    const pageType = usePageType() || FEATURED;
    const pageTypes = useAvailablePageTypes();
    const intl = useIntl();
    const [onlySearchBarIsVisible, setOnlySearchBarIsVisible] = useState<boolean>(false);

    const onlyShowSearchBar = onlySearchBarIsVisible && !isTablet;
    const isFullWidth = isMobile;

    return (
        <div className={styles.headerWrapper}>
            <div className={styles.headerInnerWrapper}>
                {onlyShowSearchBar && viewer.nftSearchEnabled ? (
                    <NftSearchBarMobile
                        isAnimatedSearchBar={!isFullWidth}
                        showScrollLockedOverlay={isFullWidth}
                        focusOnOverlayScroll={isFullWidth}
                        showArrow={isFullWidth && !onlyShowSearchBar}
                        onlySearchBarIsVisible={onlySearchBarIsVisible}
                        setOnlySearchBarIsVisible={setOnlySearchBarIsVisible}
                        viewer={viewer}
                    />
                ) : (
                    <>
                        <div className={styles.headerTopRow}>
                            <div className={styles.leftTopRow}>
                                <Link className={styles.imgLinkWrapper} href="/">
                                    {isMobile ? (
                                        <LogoMonogram className={styles.dibsLogo} />
                                    ) : (
                                        <Logo2020 className={styles.dibsLogo} />
                                    )}
                                </Link>
                                {!isMobile && viewer.nftSearchEnabled && (
                                    <NftSearchBar viewer={viewer} />
                                )}
                            </div>
                            <Link className={styles.imgLinkWrapper} href="/nft-collections">
                                <NftLogo className={styles.nftLogo} />
                            </Link>
                            <div className={styles.connectWrapper}>
                                {isMobile && viewer.nftSearchEnabled && (
                                    <Link
                                        onClick={() => setOnlySearchBarIsVisible(true)}
                                        className={styles.searchButton}
                                    >
                                        <MagnifyingGlass className={styles.icon} />
                                    </Link>
                                )}
                                <EthereumWalletStatusLazy isMobile={isMobile} />
                            </div>
                        </div>
                        <div className={styles.headerBottomRow}>
                            <NftNavBar
                                fullWidth={!isMobile}
                                align="center"
                                overflowContainer={isMobile}
                            >
                                {pageTypes
                                    .map((name: PageType) =>
                                        name !== SINGLE_EXHIBITION && name !== SEARCH ? (
                                            <li
                                                className={styles.navTabItem}
                                                key={`navTabLink-${name}`}
                                            >
                                                <Link
                                                    buyerLinkType="linkNoDecoration"
                                                    className={classnames(styles.navTabLink, {
                                                        [styles.active]:
                                                            isNftLandingPage && pageType === name,
                                                    })}
                                                    onClick={() => trackNavImpression(name)}
                                                    href={makeHref(name)}
                                                >
                                                    {intl.formatMessage(pageTypeMessages[name])}
                                                </Link>
                                            </li>
                                        ) : null
                                    )
                                    .filter(filterNulls)}
                                {viewer.nftMintBlogEnabled && (
                                    <li className={styles.navTabItem} key={`navTabLink-editorial`}>
                                        <Link
                                            className={styles.navTabLink}
                                            buyerLinkType="linkNoDecoration"
                                            href="/the-mint"
                                        >
                                            <FormattedMessage
                                                defaultMessage="Editorial"
                                                id="nft.nav.editorialLink"
                                            />
                                        </Link>
                                    </li>
                                )}
                            </NftNavBar>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
